import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, CalendarClock, FileText, Cctv, Fingerprint, Cable, Network, BellRing } from "lucide-react";
import { PHONE, PHONE_TEL, SLOGAN, HEADER_SUNSET } from "@/lib/site";

export const SERVICE_ICONS = { Cctv, Fingerprint, Cable, Network, BellRing };

export function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title;
    const m = document.querySelector('meta[name="description"]');
    if (m && description) m.setAttribute("content", description);
  }, [title, description]);
}

export function Reveal({ children, delay = 0, className = "", y = 28 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function MaskedLines({ lines, className = "", lineClassName = "", baseDelay = 0.3 }) {
  return (
    <h1 className={className}>
      {lines.map((line, i) => {
        const obj = typeof line === "string" ? { t: line } : line;
        return (
          <span key={i} className="block overflow-hidden pt-[0.06em] -mt-[0.06em] pb-[0.22em] -mb-[0.22em]">
            <motion.span
              className={`block ${obj.gold ? "text-gold-gradient" : ""} ${lineClassName}`}
              initial={{ y: "115%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.95, delay: baseDelay + i * 0.17, ease: [0.22, 1, 0.36, 1] }}
            >
              {obj.t}
            </motion.span>
          </span>
        );
      })}
    </h1>
  );
}

export function Eyebrow({ children, className = "" }) {
  return <p className={`font-mono-label text-sm uppercase tracking-[0.28em] text-gold ${className}`}>{children}</p>;
}

export function SectionHeading({ eyebrow, title, sub, align = "left", testid }) {
  return (
    <Reveal className={align === "center" ? "text-center" : ""}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 data-testid={testid} className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F4EFE6] mt-4 leading-[1.12]">
        {title}
      </h2>
      {sub && (
        <p className={`text-zinc-400 mt-5 max-w-2xl text-base leading-relaxed ${align === "center" ? "mx-auto" : ""}`}>{sub}</p>
      )}
    </Reveal>
  );
}

export function GoldButton({ to, href, children, testid, className = "" }) {
  const cls = `inline-flex items-center justify-center gap-2.5 bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] font-semibold text-sm tracking-wide px-7 py-3.5 rounded-sm transition-[transform,box-shadow] duration-300 hover:scale-[1.03] hover:shadow-[0_0_32px_rgba(212,175,55,0.35)] ${className}`;
  if (href) return <a href={href} data-testid={testid} className={cls}>{children}</a>;
  return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
}

export function GhostButton({ to, href, children, testid, className = "" }) {
  const cls = `inline-flex items-center justify-center gap-2.5 border border-gold/40 text-gold-champagne font-medium text-sm tracking-wide px-7 py-3.5 rounded-sm transition-[background-color,border-color,transform] duration-300 hover:bg-gold/10 hover:border-gold hover:scale-[1.03] ${className}`;
  if (href) return <a href={href} data-testid={testid} className={cls}>{children}</a>;
  return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
}

export function PageHero({ eyebrow, lines, sub, image, imageAlt, testid }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  return (
    <section ref={ref} data-testid={testid} className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-28">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={image} alt={imageAlt} className="w-full h-[125%] object-cover opacity-40" loading="eager" />
        <img src={HEADER_SUNSET} alt="" aria-hidden="true" className="absolute inset-0 w-full h-[125%] object-cover object-top opacity-[0.25]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/75 via-[#0A0A0C]/70 to-[#0A0A0C]" />
      </motion.div>
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
          <Eyebrow>{eyebrow}</Eyebrow>
        </motion.div>
        <MaskedLines
          lines={lines}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F4EFE6] mt-5 leading-[1.08] max-w-4xl"
        />
        {sub && (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="text-zinc-400 mt-6 max-w-2xl text-base sm:text-lg leading-relaxed"
          >
            {sub}
          </motion.p>
        )}
      </div>
      <div className="gold-hairline absolute bottom-0 left-0 right-0" />
    </section>
  );
}

export function CTABand({ title = SLOGAN, sub }) {
  return (
    <section data-testid="cta-band" className="relative border-t border-gold/15 bg-[#0D0D10] overflow-hidden">
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
        <Reveal>
          <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-gold">Redeemer Solutions LLC · Christiana, TN</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-4 leading-[1.12]">
            <span className="text-gold-gradient">{title}</span>
          </h2>
          <p className="text-zinc-400 mt-5 max-w-xl mx-auto text-base">
            {sub || "Fast diagnostics. Straight answers. Workmanship we're proud to sign our name to."}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-9">
            <GoldButton to="/request-quote" testid="cta-request-quote-button">
              <FileText size={16} /> Request a Free Quote
            </GoldButton>
            <GhostButton href={PHONE_TEL} testid="cta-call-button">
              <Phone size={16} /> Call {PHONE}
            </GhostButton>
            <GhostButton to="/schedule" testid="cta-schedule-button">
              <CalendarClock size={16} /> Schedule a Visit
            </GhostButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
